import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import type { KeyValueStorageInterface } from '@aws-amplify/core';
import { defaultStorage } from '@aws-amplify/core';
import { CookieStorage } from 'aws-amplify/utils';
import { getSharedCookieDomain } from '../routing/domains';

let isConfigured = false;

/**
 * Custom dual-layer storage implementing KeyValueStorageInterface:
 * 1. Writes to both CookieStorage (scoped to shared parent domain) and localStorage (current origin).
 * 2. Reads from CookieStorage first (enabling cross-subdomain sessions) and mirrors to localStorage.
 * 3. Falls back to localStorage and promotes any found tokens to CookieStorage.
 * 4. Removes from both on sign-out.
 */
export class CrossDomainSharedStorage implements KeyValueStorageInterface {
  readonly cookieStorage: CookieStorage;
  readonly defaultStorage: KeyValueStorageInterface;

  constructor() {
    const domain = getSharedCookieDomain();
    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    this.cookieStorage = new CookieStorage({
      domain,
      path: '/',
      expires: 30, // 30 days (1 month)
      sameSite: 'lax',
      secure: isHttps,
    });
    this.defaultStorage = defaultStorage;
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      await this.cookieStorage.setItem(key, value);
    } catch {
      // Ignore cookie write errors
    }
    try {
      await this.defaultStorage.setItem(key, value);
    } catch {
      // Ignore localStorage write errors
    }
  }

  async getItem(key: string): Promise<string | null> {
    let value: string | null = null;
    try {
      value = await this.cookieStorage.getItem(key);
    } catch {
      // Ignore cookie read error
    }

    if (value !== null && value !== undefined) {
      // Mirror to local storage so current origin's localStorage stays in sync
      try {
        await this.defaultStorage.setItem(key, value);
      } catch {
        // Ignore mirror error
      }
      return value;
    }

    // Fallback: check localStorage
    try {
      value = await this.defaultStorage.getItem(key);
    } catch {
      // Ignore localStorage read error
    }

    if (value !== null && value !== undefined) {
      // Promote from local storage to shared cookie
      try {
        await this.cookieStorage.setItem(key, value);
      } catch {
        // Ignore promote error
      }
      return value;
    }

    return null;
  }

  async removeItem(key: string): Promise<void> {
    try {
      await this.cookieStorage.removeItem(key);
    } catch {
      // Ignore
    }
    try {
      await this.defaultStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }

  async clear(): Promise<void> {
    try {
      await this.cookieStorage.clear();
    } catch {
      // Ignore
    }
    try {
      await this.defaultStorage.clear();
    } catch {
      // Ignore
    }
  }
}

/**
 * Configure Amplify Auth with CrossDomainSharedStorage scoped to the shared parent domain
 * (.staging.trade-fluency.com or .trade-fluency.com).
 * This enables cross-subdomain session recognition between public pages (e.g. staging.trade-fluency.com)
 * and application pages (e.g. dashboard.staging.trade-fluency.com).
 */
export function configureAuth(): void {
  if (isConfigured) return;

  const storage = new CrossDomainSharedStorage();

  const isTradeFluencyStaging =
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('trade-fluency.com') ||
      window.location.hostname.includes('staging'));

  const userPoolId =
    import.meta.env.VITE_COGNITO_USER_POOL_ID ||
    (isTradeFluencyStaging ? 'eu-west-2_eDaRIIc4p' : '');

  const userPoolClientId =
    import.meta.env.VITE_COGNITO_CLIENT_ID ||
    (isTradeFluencyStaging ? '1fk5jaldgmherosru8j11snrhe' : '');

  const cognitoDomain =
    import.meta.env.VITE_COGNITO_DOMAIN ||
    (isTradeFluencyStaging ? 'https://sasity-trading-staging.auth.eu-west-2.amazoncognito.com' : '');

  const authConfig: ResourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain: cognitoDomain,
            scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [
              import.meta.env.VITE_REDIRECT_SIGN_IN ?? (typeof window !== 'undefined' ? window.location.origin : ''),
            ],
            redirectSignOut: [
              import.meta.env.VITE_REDIRECT_SIGN_OUT ?? (typeof window !== 'undefined' ? window.location.origin : ''),
            ],
            responseType: 'code' as const,
          },
        },
      },
    },
  };

  Amplify.configure(authConfig);

  // Explicitly ensure cognitoUserPoolsTokenProvider has AuthConfig initialized
  // so tokenStore and tokenOrchestrator do not throw "Auth UserPool not configured"
  if (authConfig.Auth) {
    cognitoUserPoolsTokenProvider.setAuthConfig(authConfig.Auth);
  }

  // Explicitly set storage on cognitoUserPoolsTokenProvider AFTER configure so Amplify's
  // internal configure does not overwrite it with defaultStorage
  cognitoUserPoolsTokenProvider.setKeyValueStorage(storage);

  // Sync any existing legacy tokens from localStorage into the shared storage
  syncLegacyLocalStorageToCookieStorage(storage);

  isConfigured = true;
}


/**
 * Migrate any Cognito auth tokens stored in localStorage to CookieStorage.
 * Ensures users who previously logged in on the dashboard subdomain have their session
 * automatically propagated to the shared cookie domain without requiring re-login.
 */
export function syncLegacyLocalStorageToCookieStorage(storage: KeyValueStorageInterface): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('CognitoIdentityServiceProvider.')) {
        const val = localStorage.getItem(key);
        if (val) {
          storage.setItem(key, val);
        }
      }
    }
  } catch {
    // Ignore storage access issues
  }
}

/**
 * Clear any residual Cognito keys from localStorage on logout.
 */
export function clearLegacyLocalStorageAuth(): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('CognitoIdentityServiceProvider.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore storage errors
  }
}
